import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { Cookies } from '@utils/cookies'
import { ForceAny } from '@utils/typescript'
import {
  ACCESS_TOKEN,
  DO_NOT_LOGIN_KEY,
  getRefreshToken,
  loginEndpoint,
  REFRESH_TOKEN,
  refreshTokenEndpoint,
  tokenApi,
  USER_ID,
  userSessionActive,
  userSessionInactive,
  verifyEmailEndpoint,
} from './data-token'

type FailedQueue = ForceAny[]

/**
 * Client Instance
 * @description Main axios instance for API calls with interceptors
 */
export const clientInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_SERVER_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Additional Client Instance
 * @description Additional axios instance for API calls with interceptors
 */
export const additionalClientInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_ADDITIONAL_SERVER_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Base URL for internal API calls (same Next app).
 * - Client: relative '/api' so the browser uses current origin.
 * - Server: full origin so server-to-server requests reach this app.
 */
function getLocalApiBaseURL(): string {
  if (typeof window !== 'undefined') {
    return '/api'
  }

  const origin =
    process.env.NEXT_PUBLIC_URL ||
    (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : null) ||
    'http://localhost:3000'
  return `${origin.replace(/\/$/, '')}/api`
}

/**
 * Local Client Instance
 * @description Axios instance for internal API routes (agreement, typesense-import, etc.).
 * Uses full host on server so server-to-server calls work; uses '/api' on client.
 */
export const localClientInstance = axios.create({
  baseURL: getLocalApiBaseURL(),
  headers: { 'Content-Type': 'application/json' },
})

let isRefreshing = false

const failedClientQueue: FailedQueue = []
const failedAdditionalQueue: FailedQueue = []

function processQueue(error: AxiosError | null, token = null) {
  // TODO: Can be combined into one queue
  ;[failedClientQueue, failedAdditionalQueue].forEach((failedQueue) => {
    failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error)
      } else {
        promise.resolve(token)
      }
    })
    // failedQueue.length = 0
  })
}

const handleRequest = (req: InternalAxiosRequestConfig) => {
  ;[ACCESS_TOKEN, USER_ID].forEach((name) => {
    const token = Cookies.getCookie(name)
    if (token) {
      req.headers[name] = token
    }
  })
  return req
}

const handleSuccess = (res: AxiosResponse) => {
  const doNotLogin = res.config.params?.[DO_NOT_LOGIN_KEY] || false

  if (res.config.url && tokenApi.includes(res.config.url) && !doNotLogin) {
    userSessionActive(res.headers, res.data)
  }
  return res
}

function handleError(instance: AxiosInstance, failedQueue: FailedQueue) {
  return async (error: AxiosError<unknown>) => {
    const status = error.response?.status
    const originalRequest = error.config

    if (
      status === 401 &&
      originalRequest?.url &&
      ![refreshTokenEndpoint, loginEndpoint, verifyEmailEndpoint].includes(
        originalRequest.url
      )
    ) {
      /**
       * if access-token is expired, get new access-token from refresh-token and retry requests
       */
      if (isRefreshing) {
        /**
         * if refresh token api is pending, adding new request to failed queue
         */
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((accessToken) => {
            originalRequest.headers[ACCESS_TOKEN] = accessToken
            return instance(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      // Setting isRefreshing to true to prevent multiple requests
      isRefreshing = true

      return instance
        .post(refreshTokenEndpoint, {
          [REFRESH_TOKEN]: getRefreshToken(),
        })
        .then(async (res) => {
          const accessToken = res.headers[ACCESS_TOKEN]

          userSessionActive(res.headers)

          originalRequest.headers[ACCESS_TOKEN] = accessToken

          /**
           * processing all the failed request with new access token
           */
          return instance(originalRequest)
            .then((originalResponse) => {
              processQueue(null, accessToken)
              return originalResponse
            })
            .catch((originalError) => {
              processQueue(originalError, null)
              return Promise.reject(originalError)
            })
        })
        .catch((err: AxiosError) => {
          // Logout only if error if of refresh token api
          const url = err.config?.url
          if (url === refreshTokenEndpoint) {
            userSessionInactive()
          }
          return Promise.reject(err)
        })
        .finally(() => {
          isRefreshing = false
        })
    }
    return Promise.reject(error)
  }
}

clientInstance.interceptors.request.use(handleRequest)
clientInstance.interceptors.response.use(
  handleSuccess,
  handleError(clientInstance, failedClientQueue)
)

additionalClientInstance.interceptors.request.use(handleRequest)
additionalClientInstance.interceptors.response.use(
  handleSuccess,
  handleError(additionalClientInstance, failedClientQueue)
)
